<template>
    <div class="flex flex-col">
        <div class="mt-4">
            <div v-if="filteredBenefits.length === 0">
                <EmptyState
                    v-if="hasActiveFilters"
                    title="No benefits found matching filter criteria"
                    description="Try changing your filter selections."
                    :hasAction="true"
                    action-label="Clear filters"
                    @action="clearAllFilters"
                />
                <EmptyState
                    v-else
                    title="No benefits found!"
                    description="No benefits have been configured for your cards yet."
                />
            </div>

            <!-- Card Grouped View -->
            <div v-else-if="viewMode === 'by_card'" class="flex flex-col gap-4">
                <div v-for="(group, i) in filteredBenefits" :key="group.key">
                    <BenefitsCardGroupMobile
                        :card-group="group"
                        :index="i"
                        @add-usage="handleSaveUsage"
                        @view-details="handleViewDetails"
                    />
                </div>
            </div>

            <!-- Ungrouped View -->
            <div v-else class="flex flex-col gap-4">
                <div
                    v-for="(benefit, i) in filteredBenefits"
                    :key="`${benefit.benefit.id}-${benefit.connected_account_id}`"
                >
                    <BenefitsTableMobileCard
                        :benefit="benefit"
                        :index="i"
                        @add-usage="openAddUsageModal"
                        @view-details="handleViewDetails"
                        class="w-full p-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 flex flex-col gap-4 shadow-lg rounded-lg border"
                    />
                </div>
            </div>
        </div>
        <AddUsageModalMobile
            v-if="isMobile"
            :show-add-usage-modal="showAddUsageModal"
            :selected-benefit="selectedBenefit"
            @close="closeAddUsageModal"
            @add="handleSaveUsage"
        />
        <!-- Benefits Details Sidebar -->
        <BenefitDetailsSidebar v-if="sidebarOpen" />
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { storeToRefs } from 'pinia';
import { useIsMobile } from '@/composables/useIsMobile';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import BenefitsCardGroupMobile from '@/components/card-benefits/benefits-card-group-mobile.vue';
import BenefitsTableMobileCard from '@/components/card-benefits/benefits-table-mobile-card.vue';
import AddUsageModalMobile from '@/components/card-benefits/add-usage-modal-mobile.vue';
import BenefitDetailsSidebar from '@/components/card-benefits/benefit-details-sidebar.vue';

const emit = defineEmits(['usage-added', 'view-details', 'clear-filters']);

const props = defineProps({
    filteredBenefits: {
        type: Array,
        required: true,
    },
    viewMode: {
        type: String,
        required: true,
    },
    hasActiveFilters: {
        type: Boolean,
        default: false,
    },
});

// Store and composables
const store = useCardBenefitsStore();
const { isMobile } = useIsMobile();
const { showAddUsageModal, selectedBenefit, sidebarOpen } = storeToRefs(store);
const { setSelectedBenefit, setShowAddUsageModal, openBenefitDetails } = store;

// Modal handlers
const openAddUsageModal = (benefit) => {
    setSelectedBenefit(benefit);
    setShowAddUsageModal(true);
};

const closeAddUsageModal = () => {
    setShowAddUsageModal(false);
    if (!sidebarOpen.value) {
        setSelectedBenefit(null);
    }
};

const handleSaveUsage = async (usageData) => {
    // Save reference to selectedBenefit before modal closes and clears it
    const benefitRef = selectedBenefit.value;

    const success = await store.handleUsageSubmit(usageData, benefitRef);

    if (success) {
        // Update selectedBenefit to reflect the changes
        const updatedBenefit = store.benefits.find(
            (benefit) =>
                benefit.benefit.id === benefitRef.benefit.id &&
                benefit.connected_account_id === benefitRef.connected_account_id
        );
        if (updatedBenefit) {
            setSelectedBenefit(updatedBenefit);
        }

        emit('usage-added', benefitRef);
    }
};

const handleViewDetails = (benefit) => {
    openBenefitDetails(benefit);
};

const clearAllFilters = () => {
    emit('clear-filters');
};
</script>
